import { createCustomServiceCall } from '@thinknimble/tn-models'
import { z } from 'zod'
import { axiosInstance } from '../axios-instance'
import {
  guestPinSetupInputShape,
  guestPinSetupShape,
  guestSurveyContextShape,
  guestSurveyLinkShape,
} from './models'

/**
 * Fetch the token-scoped context for a guest survey link. Unauthenticated:
 * the reservation is resolved server-side from the signed token.
 */
export const getGuestSurveyContext = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string(),
    outputShape: guestSurveyContextShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/stays/guest-survey/${input}/`)
    return utils.fromApi(res.data)
  },
})

/**
 * Attach a completed survey instance to the reservation the token is scoped to.
 */
export const completeGuestSurvey = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: {
      token: z.string(),
      instanceId: z.string().uuid(),
    },
    outputShape: { id: z.string().uuid() },
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post(`/stays/guest-survey/${input.token}/complete/`, {
      instance_id: input.instanceId,
    })
    return utils.fromApi(res.data)
  },
})

/**
 * Reopen the guest's own survey instance under the token. The PIN travels only
 * in the `X-Survey-Pin` header; a wrong PIN is a 403, too many attempts a 429.
 */
export const resumeGuestSurvey = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: {
      token: z.string(),
      instanceId: z.string().uuid(),
      pin: z.string(),
    },
    outputShape: { instanceId: z.string().uuid() },
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/stays/guest-survey/${input.token}/${input.instanceId}/`, {
      headers: { 'X-Survey-Pin': input.pin },
    })
    return utils.fromApi(res.data)
  },
})

/**
 * Set the guest's PIN under the guest link. The email is required: RAPA sends the
 * resume link and PIN to it once, since the PIN cannot be recovered afterwards.
 * `emailSent` is false when that email could not be sent.
 */
export const setupGuestSurveyPin = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: guestPinSetupInputShape,
    outputShape: guestPinSetupShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post(
      `/stays/guest-survey/${input.token}/${input.instanceId}/setup-pin/`,
      { pin: input.pin, email: input.email },
    )
    return utils.fromApi(res.data)
  },
})

/**
 * Admin-only: mint a shareable guest survey link for a reservation.
 */
export const getGuestSurveyLink = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: z.string().uuid(),
    outputShape: guestSurveyLinkShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/stays/reservations/${input}/guest-survey-link/`)
    return utils.fromApi(res.data)
  },
})
